import {
  RepositoryObject,
  RepositoryTree,
  ExtendedRepositoryObject,
  RepositoryObjectFolder
} from "../backend/types";
import {
  TreeItem,
  treeItemClasses,
  TreeItemProps
} from "@mui/x-tree-view/TreeItem";
import { alpha, styled } from "@mui/material/styles";
import FileIcon from "../components/abstractComponents/fileIcon";
import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";

const DELIMITER = "__-*-__";
export const BpIcon = styled("span")(({ theme }) => ({
  borderRadius: 3,
  width: 14,
  height: 14,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 0 0 1px rgb(16 22 26 / 40%)"
      : "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
  backgroundColor: theme.palette.mode === "dark" ? "#394b59" : "#f5f8fa",
  backgroundImage:
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))"
      : "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))",
  ".Mui-focusVisible &": {
    outline: "2px auto rgba(19,124,189,.6)",
    outlineOffset: 2
  },
  "input:hover ~ &": {
    backgroundColor: theme.palette.mode === "dark" ? "#30404d" : "#ebf1f5"
  },
  "input:disabled ~ &": {
    boxShadow: "none",
    background:
      theme.palette.mode === "dark"
        ? "rgba(57,75,89,.5)"
        : "rgba(206,217,224,.5)"
  }
}));

export const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: "#137cbd",
  backgroundImage:
    "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
  "&:before": {
    display: "block",
    width: 14,
    height: 14,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""'
  },
  "input:hover ~ &": {
    backgroundColor: "#106ba3"
  }
});

// Inspired by blueprintjs
export function BpCheckbox(props: CheckboxProps) {
  return (
    <Checkbox
      sx={{
        "&:hover": { bgcolor: "transparent" },
        "& .MuiSvgIcon-root": { fontSize: 13 },
        pl: 0
      }}
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      inputProps={{ "aria-label": "Checkbox demo" }}
      {...props}
    />
  );
}

function dragThis(e: any) {
  e.dataTransfer.setData("versionId", e.target.getAttribute("data-versionid"));
  e.dataTransfer.setData(
    "absolutePath",
    e.target.getAttribute("data-absolutepath")
  );
  e.dataTransfer.setData(
    "overallValidityStatus",
    e.target.getAttribute("data-overallvaliditystatus")
  );
}

// Render Tree Nodes for the file explorer in PMX activity based on the repository objects
export const renderTreeNodes = (
  data: RepositoryObject[],
  repoId: string = "",
  currentFolderCount: RepositoryObjectFolder[] = [],
  onNodeSelect: any
) => {
  const clickShowMore = (showMore: RepositoryObjectFolder) => {
    onNodeSelect({
      path: showMore.folderName,
      repoId: showMore.repository.id,
      pageNumber: showMore.currentPageNumber + 1
    });
  };

  return data.map((item: any) => {
    const showMore: RepositoryObjectFolder =
      item.absolutePath.endsWith("/") &&
      currentFolderCount.find(
        (folder) =>
          folder.folderName === item.absolutePath &&
          folder.count > item.content.length &&
          folder.repository.id === item.repository.id
      );

    const label = (
      <div
        draggable="true"
        data-revision={item.revision}
        data-versionid={item.versionId}
        data-absolutepath={item.absolutePath}
        data-overallvaliditystatus={item.validity?.overallValidityStatus}
        onDragStart={dragThis}
      >
        <FileIcon
          fileName={item.name}
          overallValidityStatus={item.validity?.overallValidityStatus}
          fontSize={"small"}
          absolutePath={item.absolutePath}
        />
        {item.name}
      </div>
    );

    if (item.content && item.content.length > 0) {
      return (
        <>
          <StyledTreeItem
            key={repoId + DELIMITER + item.absolutePath}
            itemId={repoId + DELIMITER + item.absolutePath}
            label={label}
          >
            {renderTreeNodes(
              item.content,
              repoId,
              currentFolderCount,
              onNodeSelect
            )}
            {showMore && (
              <a
                className="btn ms-4 text-primary"
                onClick={() => clickShowMore(showMore)}
              >
                Show {showMore.count - item.content.length} more...
              </a>
            )}
          </StyledTreeItem>
        </>
      );
    }

    return (
      <>
        <StyledTreeItem
          key={repoId + DELIMITER + item.absolutePath}
          itemId={repoId + DELIMITER + item.absolutePath}
          label={label}
        >
          {item.absolutePath.endsWith("/") && ( // folders will automatically be shown as expandable, even if they are empty
            <StyledTreeItem
              key={repoId + "innerFolder" + item.absolutePath}
              itemId={repoId + "innerFolder" + item.absolutePath}
              label={<></>}
            />
          )}
        </StyledTreeItem>
      </>
    );
  });
};

// Tree view of files and folder structure within a repository (But actually rendered within a PMX activity)
export const StyledTreeItem = styled((props: TreeItemProps) => (
  <TreeItem {...props} />
))(({ theme }) => ({
  [`& .${treeItemClasses.iconContainer}`]: {
    "& .close": {
      opacity: 0.3
    }
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 9,
    paddingLeft: 5,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`
  },
  [`& .${treeItemClasses.content}`]: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 2
  }
}));

// start for ie/ee tree view
function getRepoObjectContent(
  repoObjects: ExtendedRepositoryObject[]
): ExtendedRepositoryObject[] {
  let fullFileList: ExtendedRepositoryObject[] = [];

  repoObjects?.forEach((repoObject: ExtendedRepositoryObject) => {
    fullFileList.push(repoObject);

    if (repoObject.content && repoObject.content.length > 0) {
      fullFileList = [
        ...fullFileList,
        ...getRepoObjectContent(repoObject.content)
      ];
    }
  });

  return fullFileList;
}

export function getFlattenedTree(
  tree: RepositoryTree[]
): ExtendedRepositoryObject[] {
  let fullFileList: ExtendedRepositoryObject[] = [];

  tree?.forEach((repo: any) => {
    fullFileList = [
      ...fullFileList,
      ...getRepoObjectContent(
        repo.repositoryObjects as ExtendedRepositoryObject[]
      )
    ];
  });

  return fullFileList;
}

// end for ie/ee tree view

// new way to organize the tree base on the repository objects
export function getTreeHierarchyFromRepositoryObjects(
  repositoryObjects: RepositoryObject[]
): RepositoryTree[] {
  let multiTreeStructure: RepositoryTree[] = [];
  let newMultiTreeStructure: any = [];
  repositoryObjects?.forEach((repositoryObject: any) => {
    const findTree = multiTreeStructure.find(
      (tree: RepositoryTree) =>
        tree.repository.id === repositoryObject.repository.id
    );
    if (findTree) {
      findTree.repositoryObjects.push(repositoryObject);
    } else {
      multiTreeStructure.push({
        repository: repositoryObject.repository,
        repositoryObjects: [repositoryObject]
      });
    }
  });

  const organizeTreeHierarchy = (repo: RepositoryTree) => {
    let folderStructure: any = [];
    repo.repositoryObjects?.forEach((item: ExtendedRepositoryObject) => {
      let nameSplit;
      if (item.name.endsWith("/")) {
        //otherwise we will always have an empty folder
        nameSplit = item.name.substring(0, item.name.length - 1).split("/");
      } else {
        nameSplit = item.name.split("/");
      }
      const currentName = String(nameSplit.pop());

      let currentSubArray = folderStructure;
      //for each internal folder, we parse through the tree to look for it
      let loopPath = "";
      nameSplit?.forEach((name: string) => {
        const foundObject = currentSubArray.find(
          (element: any) => element.name === name
        );
        loopPath = loopPath + name + "/"; // slash fix for when folder does not appear as separate

        if (foundObject) {
          currentSubArray = foundObject.content;
        } else {
          currentSubArray.push({
            ...item,
            name,
            content: [],
            absolutePath: loopPath
          });

          currentSubArray = currentSubArray.find(
            (element: any) => element.name === name
          ).content;
        }
      });

      const foundObject = currentSubArray.find(
        (element: any) => element.name === currentName
      );
      if (!foundObject) {
        currentSubArray.push({
          ...item,
          name: currentName,
          absolutePath: item?.name,
          content: []
        });
      } else {
        //find and replace objec in Array
        for (let i = 0; i < currentSubArray.length; i++) {
          if (currentSubArray[i].name === currentName) {
            currentSubArray[i] = {
              ...item,
              content: foundObject.content,
              name: currentName,
              absolutePath: item?.name
            };
            break;
          }
        }
      }

      currentSubArray = currentSubArray.sort(function (
        a: ExtendedRepositoryObject,
        b: ExtendedRepositoryObject
      ) {
        if (a.absolutePath.endsWith("/") && !b.absolutePath.endsWith("/")) {
          return -1;
        }
        if (b.absolutePath.endsWith("/") && !a.absolutePath.endsWith("/")) {
          return 1;
        }

        return a.name.localeCompare(b.name);
      });
    });

    newMultiTreeStructure.push({
      repository: repo.repository,
      repositoryObjects: folderStructure
    });
  };

  multiTreeStructure.forEach((tree: any) => {
    organizeTreeHierarchy(tree);
  });

  return newMultiTreeStructure;
}

export const filterTopFolderFromSelectedFiles = async (
  files: ExtendedRepositoryObject[]
): Promise<ExtendedRepositoryObject[]> => {
  return files.filter(
    (
      selected: ExtendedRepositoryObject,
      idx: number,
      arr: ExtendedRepositoryObject[]
    ) => {
      if (selected.absolutePath.endsWith("/")) {
        // filter out any folders that is a prefix of any other folders that is selected
        return !arr.some(
          (other: ExtendedRepositoryObject, otherIdx) =>
            otherIdx !== idx &&
            other.absolutePath.endsWith("/") &&
            other.absolutePath.startsWith(selected.absolutePath) &&
            other.absolutePath !== selected.absolutePath
        );
      }
      return true;
    }
  );
};

export const recursivelyGetAllFilesInFolder = async (
  folders: any[],
  callRepositoryObjects: Function
): Promise<ExtendedRepositoryObject[]> => {
  const getFolderRequest = (folder: any) => {
    const repoId = folder.repositoryId || folder.repository?.id;
    const locationPath = folder.repository?.id
      ? folder.absolutePath.replace(folder.repository?.name + "/", "")
      : folder.absolutePath.split("/").slice(1).join("/");
    return { repoId, locationPath };
  };

  const allTasks: { repoId: string; location: string; pageNumber: number }[] =
    folders.map((f) => {
      const { repoId, locationPath } = getFolderRequest(f);
      return { repoId, location: locationPath, pageNumber: 1 };
    });

  const CONCURRENCY = 5;
  const DELAY_MS = 3000;
  const results: ExtendedRepositoryObject[] = [];
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  let currentIndex = 0;
  while (currentIndex < allTasks.length) {
    const batch = allTasks.slice(currentIndex, currentIndex + CONCURRENCY);

    const batchPromises = batch.map(async (task) => {
      const { data } = await callRepositoryObjects({
        variables: {
          repoId: task.repoId,
          location: task.location,
          recursive: true,
          pageNumber: task.pageNumber
        }
      });

      if (!data) return [];

      const totalFiles = data.getRepositoryObjects.totalCount;
      const files: RepositoryObject[] =
        data.getRepositoryObjects.repositoryObjects || [];
      const newObjects = files.map((file) => ({
        ...file,
        absolutePath: `${file.repository.name}/${file.name}`
      }));

      const currentCount = files.length || 0;
      if (currentCount < totalFiles) {
        allTasks.push({
          repoId: task.repoId,
          location: task.location,
          pageNumber: task.pageNumber + 1
        });
      }
      return newObjects;
    });

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults.flat());
    currentIndex += CONCURRENCY;

    if (currentIndex < allTasks.length) {
      await delay(DELAY_MS);
    }
  }

  return results;
};
